import { getAuth } from 'firebase/auth';
import axios, { AxiosError } from 'axios';
import { FIREBASE_FUNCTION_URL } from '../app-settings';
import { ArchetypeFunctionRequest } from '../models/CloudFunction';
import { Archetype } from '../models/Archetype';
import { SearchParams, SearchResponse } from 'typesense/lib/Typesense/Documents';
import { Identifiable } from '../models/Common';

export async function publishArchetype(item: Archetype): Promise<Archetype> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();
    const requestBody: ArchetypeFunctionRequest<Archetype> = {
        functionName: "publish",
        body: item
    };

    try {
        const response = await axios.post(`${FIREBASE_FUNCTION_URL}/archetypes`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
        return response.data as Archetype;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError && axiosError.isAxiosError && axiosError.response && axiosError.response.status === 409) {
            return Promise.reject("An archetype with this name already exists.");
        }

        return Promise.reject("Error publishing archetype.");
    }
}

export async function searchArchetypes(params: SearchParams): Promise<SearchResponse<Archetype>> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const searchParams: SearchParams = {
        ...params,
        query_by: "name",
        sort_by: "name:asc"
    };

    const requestBody: ArchetypeFunctionRequest<SearchParams> = {
        functionName: "search",
        body: searchParams
    };

    const response = await axios.post(`${FIREBASE_FUNCTION_URL}/archetypes`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
    return response.data as SearchResponse<Archetype>;
}

export async function getArchetype(props: Identifiable): Promise<Archetype> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const requestBody: ArchetypeFunctionRequest<Identifiable> = {
        functionName: "get",
        body: { id: props.id }
    };

    const response = await axios.post(`${FIREBASE_FUNCTION_URL}/archetypes`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
    return response.data as Archetype
}

export async function deleteArchetype(props: Identifiable): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser) {
        return Promise.reject();
    }

    const userToken = await auth.currentUser.getIdToken();

    const requestBody: ArchetypeFunctionRequest<Identifiable> = {
        functionName: "delete",
        body: { id: props.id }
    };

    await axios.post(`${FIREBASE_FUNCTION_URL}/archetypes`, requestBody, { headers: { Authorization: `Bearer ${userToken}` } });
}

export async function getArchetypeNames(): Promise<{ id: string, name: string }[]> {
    const requestBody: ArchetypeFunctionRequest<null> = {
        functionName: "getnames",
        body: null
    };

    const response = await axios.post(`${FIREBASE_FUNCTION_URL}/archetypes`, requestBody);
    const data = response.data as { id: string, name: string }[];

    data.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA < nameB) {
            return -1;
        }

        if (nameA > nameB) {
            return 1;
        }

        return 0;
    });

    return data;
}